import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { fm } from 'Components/FormatMessage';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import { resMedSettingsFieldToRangeMap } from '../../config/sleepApnea';
import { capitalize } from 'Utility/string';
import { StyledTitle } from '..';

const fields = ['ipap', 'epap', 'pressureSupport', 'respiratoryRate'];

const PapSettingsField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const thisSearchField =
    searchFields.papSettings && searchFields.papSettings !== 'noCropping' ? searchFields.papSettings : undefined;

  const fieldToRangeMap = resMedSettingsFieldToRangeMap;

  const handleChange = (values: TOnChangeValues) => {
    const f = Object.keys(values)[0];
    let v = values[f];

    if (f !== 'timeframe' && !Array.isArray(v)) v = 'noCropping';
    if (Array.isArray(v) && v.every(vv => typeof vv === 'number')) {
      const range = fieldToRangeMap.get(f) ?? [0, 0];

      const newMin = (v as number[])[0];
      const newMax = (v as number[])[1];

      if (newMin === range[0] && newMax === range[1]) v = 'noCropping';
      else
        v = {
          [`min${f === 'respiratoryRate' ? capitalize(f) : f.toUpperCase()}`]: newMin,
          [`max${f === 'respiratoryRate' ? capitalize(f) : f.toUpperCase()}`]: newMax,
        };
    }

    const baseValue = typeof thisSearchField === 'object' ? thisSearchField : {};
    const newValues: Partial<typeof thisSearchField> = { ...baseValue, [f]: v };

    onChange({ papSettings: newValues });
  };

  return (
    <React.Fragment>
      <StyledTitle>{fm('extendedSearch.timeframe')}</StyledTitle>
      <InputHandler
        type="Radio"
        editing={true}
        name="timeframe"
        formData={{
          onChange: handleChange,
          document: { timeframe: thisSearchField?.timeframe },
        }}
        options={['last12months', 'last6months', 'last3months', 'last1months']}
        optionFormatter={(name: string | number): string | React.JSX.Element => fm(`extendedSearch.opts.${name}`)}
      />
      {thisSearchField?.timeframe &&
        fields.map((f, i) => {
          const v = thisSearchField?.[f as keyof typeof thisSearchField];
          const range = fieldToRangeMap.get(f) ?? [0, 0];
          return (
            <React.Fragment key={`${f}-${i}`}>
              <StyledTitle>{fm(`extendedSearch.${f}`)}</StyledTitle>
              <div style={{ paddingRight: '4.2rem' }}>
                <InputHandler
                  key={`${f}-${i}-rangeslider`}
                  type="RangeSlider"
                  editing={true}
                  name={f}
                  formData={{
                    onChange: handleChange,
                    document: {
                      [f]:
                        v === 'noCropping'
                          ? undefined
                          : v && typeof v === 'object'
                            ? [Object.values(v as TAnyObject)[0], Object.values(v as TAnyObject)[1]]
                            : undefined,
                    },
                  }}
                  min={range[0]}
                  max={range[1]}
                  showRangeText={true}
                />
              </div>
            </React.Fragment>
          );
        })}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default PapSettingsField;
